import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const MicrosoftCloudSolutions = () => {
  const phrases = [
    "Azure Cloud Architectures",
    "Dynamics 365 Core Sync",
    "Enterprise .NET Microservices"
  ];

  const cardData = [
    { type: 'ms-core', content: '🔷' },
    { type: 'teal-wave', content: '📊' },
    { type: 'azure-stack', content: '⚙️' },
    { type: 'dot-net-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🔷", title: "Azure Cloud Infrastructure & Architecture", desc: "Design, build, and scale resilient cloud topologies using Azure Kubernetes Service (AKS), secure Virtual Networks (VNet), and optimized App Service resources." },
    { icon: "⚡", title: "Enterprise .NET Microservices Development", desc: "Architect high-throughput, cross-platform backend APIs using .NET 8/9, Entity Framework Core, and distributed message brokers like Azure Service Bus." },
    { icon: "⚙️", title: "Dynamics 365 (F&O / CE) Implementation", desc: "Unify strategic global financial structures and customer interaction matrices by deploying highly customized Dynamics 365 environments." },
    { icon: "📊", title: "Fabric & Synapse Data Analytics Hubs", desc: "Ingest, transform, and map high-volume corporate operational data into direct, real-time dashboard analytics using Microsoft Fabric pipelines." },
    { icon: "🔗", title: "Power Platform Declarative Automation", desc: "Build enterprise-grade internal application states and multi-tier workflows using Power Apps, Power Automate, and Dataverse engines." },
    { icon: "🛡️", title: "Microsoft Entra Identity & Zero-Trust Security", desc: "Enforce strict conditional access parameters, seamless Single Sign-On (SSO) links, and continuous real-time threat governance profiles." },
    { icon: "🔄", title: "DevOps & GitHub Git-Ops Pipelines", desc: "Establish automated source-controlled release configurations via Azure Pipelines, infrastructure-as-code (Bicep/Terraform), and rigid validation testing gates." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "Azure OpenAI & Copilot Customization", desc: "Incorporate intelligent retrieval-augmented generation (RAG) capabilities directly within corporate internal tools via Azure AI Studio nodes safely." },
    { icon: "💼", title: "SQL Server High-Availability Cluster Tuning", desc: "Optimize heavy relational database instances using Always On availability groups, intelligent index partitioning, and performance tracking." },
    { icon: "🗃️", title: "Cosmos DB Distributed NoSQL Pipelines", desc: "Configure global, low-latency document storage systems engineered for continuous horizontal scaling and flexible consistency configurations." },
    { icon: "⚖️", title: "Azure Functions & Serverless Architectures", desc: "Construct resource-efficient, event-driven compute tasks to process files, webhooks, and transactional data patterns instantly." },
    { icon: "🚨", title: "Microsoft Sentinel SIEM Proactive Monitoring", desc: "Centralize and monitor security telemetry across multi-cloud enterprise footprints to mitigate structural vulnerabilities instantly." },
    { icon: "📝", title: "API Management (APIM) Policy Orchestration", desc: "Govern, rate-limit, and secure distributed backend application endpoints utilizing centralized cloud proxy interface gateways." },
    { icon: "🌐", title: "Hybrid Cloud Azure Arc Infrastructures", desc: "Extend native Azure governance, management, and compliance tools smoothly onto legacy on-premise hardware footprints." },
    { icon: "🧪", title: "Automated Playwright Integration Testing", desc: "Program automated regression test matrices to validate distributed frontend and backend stability patterns prior to production deployment." },
    { icon: "📦", title: "Azure Site Recovery Disaster Blueprints", desc: "Design failover strategies using automated geo-redundant storage syncing to guarantee strict business operation continuity parameters." }
  ];

  const technologies = {
    "Cloud Infrastructure Azure": ["Azure Kubernetes Service (AKS)", "Azure App Services & Functions", "Azure Virtual Networks & Firewalls", "Azure Arc Hybrid Control Systems"],
    "Enterprise Programming": [".NET 8/9 / ASP.NET Core Frameworks", "C# / Entity Framework Core", "Azure Service Bus / Event Hubs", "RESTful APIs / gRPC Communication"],
    "Business Applications": ["Dynamics 365 Finance & Operations", "Dynamics 365 Customer Engagement", "Power Apps / Power Automate / Dataverse", "Microsoft 365 Modern Workplace"],
    "Data & Analytics Platform": ["Microsoft Fabric Unified Analytics", "Azure Synapse / Data Factory Pipelines", "Azure SQL Server / Azure Cosmos DB", "Power BI Embedded Lenses & Dashboards"],
    "DevOps & Security Controls": ["Azure DevOps / GitHub Actions Pipelines", "Infrastructure as Code (Bicep / Terraform)", "Microsoft Entra ID / Conditional Access", "Microsoft Sentinel SIEM / Defender"]
  };

  const processSteps = [
    { num: "01", title: "Architecture & Code Discovery", desc: "Evaluating legacy application dependencies, checking hardware capacities, and identifying operational system technical debt." },
    { num: "02", title: "Target Cloud Schema Blueprinting", desc: "Structuring secure VNet subnets, mapping microservice domains, and defining database scale-out parameters." },
    { num: "03", title: "CI/CD Pipeline & Code Synthesis", desc: "Assembling programmatic backend engines, establishing infrastructure script structures, and building out source repositories." },
    { num: "04", title: "Dynamics & Data Integration Hubs", desc: "Mapping transactional custom schemas, configuring web API pathways, and handling corporate payload flows." },
    { num: "05", title: "Security Entra ID & Access Audits", desc: "Validating functional RBAC assignments, testing authentication loops, and executing high-load performance runs." },
    { num: "06", title: "Automated Release Package Push", desc: "Executing automated regression test suites and pushing code artifacts to target instances smoothly using GitOps pipelines." },
    { num: "07", title: "App Insights & Optimization Loops", desc: "Reviewing execution performance metrics via Azure Monitor, tracing API transaction delays, and applying performance patches." }
  ];

  const industries = [
    "High-Velocity Financial Banking & FinTech Solutions", "Global Supply Chain Logistics & Inventory Operations", "Cross-Border Specialized Healthcare & Digital Records",
    "High-Density Retail, E-Commerce & Inventory Platforms", "Aerospace, Heavy Automotive & Component Assembly", "High-Growth Enterprise Software-as-a-Service (SaaS)",
    "Commercial Real Estate & Structural Asset Management", "Public Sector Government Agency Cloud Transformations", "Energy Infrastructure, Utilities & Allocation Networks",
    "Global Maritime Shipping, Cargo & Transport Systems", "Media Networks & High-Scale Digital Content Delivery", "Higher Education Multi-Campus Student Lifecycle Systems"
  ];

  const designBenefits = [
    "Zero Financial Transactional Reconciliation Variances", "Up to 55% Drop in API Execution and Response Latency", "100% Secure, Zero-Trust Enterprise Cloud Architectures",
    "Guaranteed 99.99% Cloud Service Environment Availability", "Drastic Drop in Manual Deployment and Infrastructure Tasks", "Highly Scalable Containerized Microservices Implementations",
    "Seamless Cross-System Data Synchronization Pipelines", "Real-Time Enterprise Performance Dashboard Metrics Ingestion"
  ];

  const whyChooseUs = [
    "Certified Microsoft Solutions & Azure Enterprise Architects", "Proven Scaling Track Record Across Massive Cloud Workloads", "Airtight Zero-Trust Entra Identity Custom Governance",
    "Deep Competency in Complex .NET Core Backend Optimization", "Platform-Agnostic Hybrid System Interfacing Competency", "Resilient High-Availability AKS Cluster Infrastructure Setups",
    "End-to-End Environment Transport & Lifespan CI/CD Governance", "Transparent Engineering Goals & Verifiable Sprint Testing Metrics", "High-Availability Platform Integration Infrastructure SLAs",
    "Experienced Dynamics 365 & Power Platform Architecture Experts", "Iterative Sandbox Environment Prototyping Validation Sprints", "100% Production Environment Architecture Stability Assurances"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Microsoft Cloud Solutions | Azure Cloud Consulting & Microsoft Services | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Microsoft Cloud Solutions | Azure Cloud Consulting & Microsoft Services | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides Microsoft Cloud Solutions, including Microsoft Azure consulting, Azure cloud migration, Microsoft 365, cloud infrastructure, Azure DevOps, cloud security, hybrid cloud solutions, and enterprise cloud transformation services."
        />

        <meta
          name="keywords"
          content="Microsoft Cloud Solutions, Microsoft Azure, Azure Cloud Services, Azure Consulting, Azure Cloud Migration, Microsoft 365, Azure DevOps, Azure Infrastructure, Cloud Security, Hybrid Cloud Solutions, Azure Managed Services, Microsoft Cloud Consulting, Enterprise Cloud Solutions, Cloud Transformation, Microsoft Azure Implementation, Microsoft Cloud Services, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/microsoft-cloud-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Microsoft Cloud Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Accelerate your digital transformation with Microsoft Cloud Solutions from Affsoft IT Solutions Pvt. Ltd. We specialize in Microsoft Azure, Azure DevOps, Microsoft 365, cloud migration, cloud security, and enterprise cloud services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/microsoft-cloud-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Microsoft Cloud Solutions | Azure Cloud Consulting & Microsoft Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Modernize your business with Microsoft Cloud Solutions from Affsoft IT Solutions Pvt. Ltd. We provide Azure consulting, Microsoft 365, Azure DevOps, cloud migration, hybrid cloud, and cloud security services."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="microsoft-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#071630' // Deep Microsoft navy slate fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Microsoft &nbsp; Cloud &nbsp; Platforms &nbsp; &amp; &nbsp;</span>
              <span>Enterprise Azure Architectures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-teal fs-3" style={{ color: '#00a3a4' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Microsoft Cloud Platforms</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the architectural bottlenecks that limit enterprise system agility. Our Microsoft systems engineering practice pairs resilient Azure cloud modernization with optimized .NET microservice layers and Dynamics 365 environments to convert core system footprints into high-speed strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose a Cloud Blueprint</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#00a3a4' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#00a3a4' }}>Cloud Infrastructure Tuning</span>
              <h2 className="fw-bold text-dark display-6">Deploy High-Throughput Compute Frameworks & Upgrade-Stable Automation</h2>
              <p className="text-muted mt-3">
                Monolithic application structures, fragile manual configuration flows, and disconnected backend databases create operational limits. At Affsoft IT Solutions Pvt. Ltd., we construct airtight, zero-downtime, and systematically tracked Microsoft cloud ecosystems.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#00a3a4' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise Microsoft Modernization</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#00a3a4' }}>Distributed System Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Platform Capabilities Built for High Transaction Volume</h2>
              <p className="text-muted mt-3">
                We scale standard cloud layouts into dynamic, automated software systems. From optimizing distributed serverless workloads to deploying secure Azure AI instances, our solutions guarantee absolute system durability.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#00a3a4' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#00a3a4' }}>Microsoft Architecture Matrix</span>
              <h2 className="fw-bold display-6">Infrastructure Systems & Programming Toolsets We Standardize</h2>
              <p className="text-light-50">We configure, interface, and manage high-volume application networks utilizing strict validation matrices.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#00a3a4' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#00a3a4' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Environment Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Cloud Deployment Blueprint</h2>
              <p className="text-muted">A structured, strictly governed development lifecycle designed to transition heavy core processes safely from staging phases onto active live production instances.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#00a3a4' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#00a3a4' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine programmatic software mastery with robust deployment pipeline automation to guarantee stable enterprise software behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#00a3a4' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default MicrosoftCloudSolutions;